import { useEffect, useId, useState } from 'react';
import type { ReactNode } from 'react';
import { useAction, useApi } from '../../context';
import { Failure, Rich } from '../../bits';
import { useT } from '../../../messages';
import type { BrandingChecked, BrandingDocument, BrandingPublished } from '../../../wire';
import { SiteDialog } from '../site/SiteDialog';

/**
 * **Publish** — check first, in words, then publish with a short note.
 *
 * The check is the publish's own validator (`POST …/branding/check`), so what
 * this dialog says before the click is what the publish would have said
 * after it: a refusal in the server's sentence, with Publish unavailable
 * until it is fixed; the warnings the page would go out with; what the
 * sanitizer will take out of a content zone. Only then the one button that
 * writes — and it says what that means for the business: every guest sees it
 * on their next page load, no build, no deploy.
 *
 * A bar entry pointing at a page this document does not declare never reaches
 * the server (the old editor's one client-side refusal, kept): it is said
 * here, before the check, in the server's own words.
 */
export function PublishDialog({
  tenantId,
  name,
  document,
  blocked,
  onPublished,
  onClose,
}: {
  tenantId: string;
  name: string;
  document: BrandingDocument;
  /** A sentence saying why this cannot be sent at all, or `null`. */
  blocked: string | null;
  onPublished: (answer: BrandingPublished) => void;
  onClose: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const noteId = useId();
  const check = useAction();
  const publish = useAction();
  const [checked, setChecked] = useState<BrandingChecked | null>(null);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (blocked !== null) return;
    void check.run(async () => {
      setChecked(await api.checkBranding(tenantId, document));
    });
    // Once per opening: the document cannot change while the dialog is up.
  }, []);

  const go = async (): Promise<void> => {
    let answer: BrandingPublished | null = null;
    const ok = await publish.run(async () => {
      answer = await api.publishBranding(tenantId, document, note.trim());
    });
    if (ok && answer !== null) onPublished(answer);
  };

  const cleaned = checked?.zones.filter((zone) => zone.changed) ?? [];
  const ready = blocked === null && checked !== null && check.failure === null;
  const busy = check.busy || publish.busy;

  return (
    <SiteDialog title={t('builder.publish.title', { name })} busy={publish.busy} onClose={onClose}>
      {blocked !== null ? (
        <p className="error-text" role="alert">
          <Rich text={blocked} />
        </p>
      ) : null}

      {check.busy ? <p className="bld-hint">{t('builder.publish.checking')}</p> : null}
      {check.failure !== null ? (
        <div role="alert">
          <p className="bld-warn">{t('builder.publish.refused')}</p>
          <Failure failure={check.failure} />
        </div>
      ) : null}

      {checked === null ? null : (
        <div className="bld-stack" role="status">
          {checked.warnings.length === 0 && cleaned.length === 0 ? (
            <p className="ok">{t('builder.publish.clean')}</p>
          ) : null}
          {checked.warnings.length === 0 ? null : (
            <>
              <p>{t('builder.publish.warnings')}</p>
              <ul className="bld-plain">
                {checked.warnings.map((warning) => (
                  <li key={warning} className="warn">
                    {warning}
                  </li>
                ))}
              </ul>
            </>
          )}
          {cleaned.length === 0 ? null : (
            <>
              <p>{t('builder.publish.cleaned', { count: cleaned.length })}</p>
              <ul className="bld-plain">
                {cleaned.map((zone) => (
                  <li key={zone.path}>
                    {t('builder.publish.cleanedZone', { removed: zone.removed.join(', ') })}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <div className="bld-field">
        <label htmlFor={noteId}>{t('builder.publish.note')}</label>
        <input
          id={noteId}
          className="bld-input"
          value={note}
          maxLength={400}
          placeholder={t('branding.publishNotePlaceholder')}
          onChange={(e) => setNote(e.target.value)}
        />
        <span className="bld-hint">{t('builder.publish.noteHint')}</span>
      </div>

      <p className="modal__consequence">{t('builder.publish.consequence')}</p>
      {publish.failure !== null ? <Failure failure={publish.failure} /> : null}

      <div className="modal__actions">
        <button type="button" className="button" disabled={publish.busy} onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="button button--primary"
          disabled={!ready || busy}
          onClick={() => void go()}
        >
          {publish.busy ? t('common.working') : t('builder.publish.go')}
        </button>
      </div>
    </SiteDialog>
  );
}
